import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Copy, Eye, EyeOff, RefreshCw, AlertTriangle } from 'lucide-react'

interface ApiKeyDisplayProps {
  appId: string
  newKey?: string | null
  rotateUrl: string
}

export function ApiKeyDisplay({ appId, newKey, rotateUrl }: ApiKeyDisplayProps) {
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)
  const [rotateOpen, setRotateOpen] = useState(false)

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleRotate() {
    setRotateOpen(false)
    router.post(rotateUrl)
  }

  return (
    <div className="space-y-4">
      {/* App ID — always shown */}
      <div className="space-y-1">
        <Label>App ID</Label>
        <div className="flex items-center gap-2">
          <Input readOnly value={appId} className="font-mono text-sm" />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(appId)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy App ID</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Secret key section */}
      <div className="space-y-1">
        <Label>Secret Key</Label>

        {newKey ? (
          <div className="space-y-3">
            <Alert variant="default" className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700 dark:text-amber-400">
                <strong>Copy this key now.</strong> It will not be shown again.
              </AlertDescription>
            </Alert>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={revealed ? newKey : newKey.replace(/./g, '•')}
                className="font-mono text-sm"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setRevealed((v) => !v)}
                    >
                      {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{revealed ? 'Hide' : 'Reveal'}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(newKey)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{copied ? 'Copied!' : 'Copy secret key'}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Input
              readOnly
              value="sk_live_••••••••••••••••••••••••"
              className="font-mono text-sm text-muted-foreground"
            />
            <Dialog open={rotateOpen} onOpenChange={setRotateOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Rotate Key
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Rotate secret key</DialogTitle>
                  <DialogDescription>
                    This will immediately invalidate your current secret key. Any integrations
                    using it will break until updated. This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRotateOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleRotate}>
                    Rotate key
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </div>
  )
}
